/**
 * NotificationCenter Component
 * Real-time notifications via WebSocket + persistent storage in MongoDB.
 * Features: DB persistence, sound alerts, push notifications, date grouping.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Bell, BellOff, X, Play, Square, Calendar, RefreshCw,
  MessageCircle, CheckCircle, XCircle, MapPin, Clock,
  Volume2, VolumeX, Trash2, Check, BellRing
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { usePermissions } from "../hooks/usePermissions";
import { API, BACKEND_URL } from "../config/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Web Audio API sound generation
const createAudioContext = () => {
  try { return new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { return null; }
};

const playNotificationSound = (type) => {
  const ctx = createAudioContext();
  if (!ctx) return;
  const isUrgent = ["work_started", "work_ended"].includes(type);
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  if (isUrgent) {
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } else {
    osc.type = "sine";
    osc.frequency.setValueAtTime(700, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
  }
  setTimeout(() => ctx.close(), 500);
};

// Push subscription helpers
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

const NotificationCenter = ({ user, onConnectionChange }) => {
  const { canReceiveNotifications } = usePermissions();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem("lojack_notification_sound") !== "false";
  });

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // Toggle sound
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newVal = !prev;
      localStorage.setItem("lojack_notification_sound", String(newVal));
      if (newVal) playNotificationSound("new_appointment");
      return newVal;
    });
  };

  // Propagate connection state to parent
  useEffect(() => {
    if (onConnectionChange) onConnectionChange(isConnected);
  }, [isConnected, onConnectionChange]);

  // Push notifications management
  const checkPushStatus = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC_KEY) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
    } catch (e) {
      console.error("Push check error:", e);
    }
  }, []);

  const togglePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error("Push notifications non supportate dal browser");
      return;
    }
    setPushLoading(true);
    const token = localStorage.getItem("token");
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushEnabled) {
        // Unsubscribe
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe`, { headers: { Authorization: `Bearer ${token}` } });
        setPushEnabled(false);
        toast.success("Notifiche push disattivate");
      } else {
        // Subscribe
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error("Permesso notifiche negato dal browser");
          setPushLoading(false);
          return;
        }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
        const subJson = sub.toJSON();
        await axios.post(`${API}/push/subscribe`,
          { endpoint: subJson.endpoint, keys: subJson.keys },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPushEnabled(true);
        toast.success("Notifiche push attivate");
      }
    } catch (e) {
      console.error("Push toggle error:", e);
      toast.error("Errore gestione notifiche push");
    } finally {
      setPushLoading(false);
    }
  };

  // Load from DB
  const fetchNotifications = useCallback(async () => {
    if (!canReceiveNotifications) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications.map(n => ({
        id: n.id, ...n.data, read: n.read, timestamp: n.data.timestamp || n.created_at, _dbId: n.id
      })));
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error("Error fetching notifications:", e);
    } finally {
      setLoading(false);
    }
  }, [canReceiveNotifications]);

  // WebSocket with improved reliability
  const connectWebSocket = useCallback(() => {
    if (!canReceiveNotifications) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { setIsConnected(true); reconnectAttemptsRef.current = 0; };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "connected" || data.type === "ping" || data.type === "pong") return;
          fetchNotifications();
          if (soundEnabled) playNotificationSound(data.type);
          showToast(data);
        } catch (e) { console.error("WS parse error:", e); }
      };
      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        // Reconnect with exponential backoff, max 15 attempts, capped at 60s
        if (reconnectAttemptsRef.current < 15 && event.code !== 4001 && event.code !== 4003) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 60000);
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };
      ws.onerror = () => {};
    } catch (e) { console.error("WS create error:", e); }
  }, [canReceiveNotifications, soundEnabled]);

  // Reconnect when tab becomes visible or network comes back online
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && canReceiveNotifications) {
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          reconnectAttemptsRef.current = 0;
          connectWebSocket();
        }
        fetchNotifications();
      }
    };
    const handleOnline = () => {
      if (canReceiveNotifications) {
        reconnectAttemptsRef.current = 0;
        connectWebSocket();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
    };
  }, [canReceiveNotifications, connectWebSocket, fetchNotifications]);

  // CRUD
  const markAsRead = async (notifId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/notifications/${notifId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n._dbId === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error("Error marking read:", e); }
  };

  const markAllAsRead = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.patch(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { console.error("Error marking all read:", e); }
  };

  const deleteNotification = async (notifId) => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/notifications/${notifId}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => {
        const n = prev.find(x => x._dbId === notifId);
        if (n && !n.read) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(x => x._dbId !== notifId);
      });
    } catch (e) { console.error("Error deleting:", e); }
  };

  const clearAllNotifications = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) { console.error("Error clearing:", e); }
  };

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    notifications.forEach(n => {
      const date = new Date(n.timestamp);
      const dateStr = date.toDateString();
      let label;
      if (dateStr === today) label = "Oggi";
      else if (dateStr === yesterday) label = "Ieri";
      else label = date.toLocaleDateString("it-IT", { day: "numeric", month: "long" });

      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return Object.entries(groups);
  }, [notifications]);

  // Toast
  const showToast = (data) => {
    const icons = {
      work_started: <Play className="text-green-500 flex-shrink-0 mt-0.5" size={18} />,
      work_ended: data.status === "completed" ? <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={18} /> : <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />,
      new_appointment: <Calendar className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />,
      appointment_updated: <RefreshCw className="text-indigo-500 flex-shrink-0 mt-0.5" size={18} />,
      appointment_deleted: <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />,
      appointment_assigned: <MapPin className="text-teal-500 flex-shrink-0 mt-0.5" size={18} />,
      status_changed: <RefreshCw className="text-orange-500 flex-shrink-0 mt-0.5" size={18} />,
      chat_message: <MessageCircle className="text-purple-500 flex-shrink-0 mt-0.5" size={18} />,
    };
    const titles = {
      work_started: `${data.installer_name} ha iniziato`, work_ended: `${data.installer_name} ha terminato`,
      new_appointment: "Nuovo appuntamento", appointment_updated: "Appuntamento modificato",
      appointment_deleted: "Appuntamento eliminato", appointment_assigned: "Installatore assegnato",
      status_changed: "Stato aggiornato", chat_message: data.sender_name,
    };
    const descs = {
      work_started: data.client_name,
      work_ended: `${data.status === "completed" ? "Completato" : "Non completato"}${data.duration_minutes ? ` - ${data.duration_minutes}min` : ""}`,
      new_appointment: `${data.client_name} - ${data.scheduled_time}`,
      appointment_updated: `${data.client_name} da ${data.updated_by || ""}`,
      appointment_deleted: `${data.client_name} da ${data.deleted_by || ""}`,
      appointment_assigned: `${data.installer_name} a ${data.client_name}`,
      status_changed: `${data.client_name}: ${data.new_status}`,
      chat_message: data.message_preview,
    };
    const icon = icons[data.type];
    if (!icon) return;
    toast.info(
      <div className="flex items-start gap-2">{icon}<div><p className="font-medium">{titles[data.type]}</p><p className="text-sm text-gray-500">{descs[data.type]}</p></div></div>,
      { duration: 5000 }
    );
  };

  // Display helpers
  const getIcon = (type) => {
    const map = {
      work_started: <Play className="text-green-500" size={16} />, work_ended: <Square className="text-red-500" size={16} />,
      new_appointment: <Calendar className="text-blue-500" size={16} />, appointment_updated: <RefreshCw className="text-indigo-500" size={16} />,
      appointment_deleted: <XCircle className="text-red-500" size={16} />, appointment_assigned: <MapPin className="text-teal-500" size={16} />,
      status_changed: <RefreshCw className="text-orange-500" size={16} />, chat_message: <MessageCircle className="text-purple-500" size={16} />,
    };
    return map[type] || <Bell className="text-gray-500" size={16} />;
  };

  const getTitle = (n) => {
    const map = {
      work_started: `${n.installer_name} ha iniziato il lavoro`, work_ended: `${n.installer_name} ha terminato il lavoro`,
      new_appointment: "Nuovo appuntamento creato", appointment_updated: "Appuntamento modificato",
      appointment_deleted: "Appuntamento eliminato", appointment_assigned: "Installatore assegnato",
      status_changed: "Stato appuntamento aggiornato", chat_message: `Messaggio da ${n.sender_name}`,
    };
    return map[n.type] || "Notifica";
  };

  const getDesc = (n) => {
    switch (n.type) {
      case "work_started": return `${n.client_name} - ${n.address || ""}`;
      case "work_ended": return `${n.client_name} - ${n.status === "completed" ? "Completato" : "Non completato"}${n.duration_minutes ? ` (${n.duration_minutes}min)` : ""}`;
      case "new_appointment": return `${n.client_name} - ${n.scheduled_time} (da ${n.created_by || ""})`;
      case "appointment_updated": return `${n.client_name} - da ${n.updated_by || ""}`;
      case "appointment_deleted": return `${n.client_name} - da ${n.deleted_by || ""}`;
      case "appointment_assigned": return `${n.installer_name} assegnato a ${n.client_name}`;
      case "status_changed": return `${n.client_name}: ${n.old_status} -> ${n.new_status}`;
      case "chat_message": return n.message_preview;
      default: return "";
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const diff = Date.now() - date;
    if (diff < 60000) return "ora";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min fa`;
    if (diff < 86400000) return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("it-IT");
  };

  // Lifecycle
  useEffect(() => {
    fetchNotifications();
    connectWebSocket();
    checkPushStatus();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket, fetchNotifications, checkPushStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send("ping");
      } else if (canReceiveNotifications && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
        reconnectAttemptsRef.current = 0;
        connectWebSocket();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [canReceiveNotifications, connectWebSocket]);

  if (!canReceiveNotifications) return null;

  return (
    <div className="relative" data-testid="notification-center">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition ${isOpen ? "bg-gray-200" : "hover:bg-gray-100"}`}
        data-testid="notification-bell"
      >
        {isConnected ? <Bell size={22} className="text-gray-700" /> : <BellOff size={22} className="text-gray-400" />}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        <span className={`absolute bottom-1 right-1 h-2 w-2 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`}></span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          <div className="fixed inset-x-3 top-16 z-50 md:absolute md:inset-x-auto md:right-0 md:top-12 md:w-96 bg-white rounded-xl shadow-2xl border overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={20} className="text-gray-600" />
                <h3 className="font-semibold text-gray-800">Notifiche</h3>
                {!isConnected && <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded">Offline</span>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={togglePush} disabled={pushLoading} title={pushEnabled ? "Push attive" : "Attiva push"}
                  className={`p-1.5 rounded-lg transition ${pushEnabled ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}`}
                  data-testid="push-toggle">
                  {pushLoading ? <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div> : <BellRing size={16} />}
                </button>
                <button onClick={toggleSound} title={soundEnabled ? "Suono attivo" : "Suono disattivato"}
                  className={`p-1.5 rounded-lg transition ${soundEnabled ? "text-blue-600 hover:bg-blue-50" : "text-gray-400 hover:bg-gray-100"}`}
                  data-testid="sound-toggle">
                  {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                </button>
                {notifications.length > 0 && (
                  <>
                    <button onClick={markAllAsRead} className="p-1.5 rounded-lg hover:bg-gray-100 transition" title="Segna tutte lette" data-testid="mark-all-read">
                      <Check size={16} className="text-blue-600" />
                    </button>
                    <button onClick={clearAllNotifications} className="p-1.5 rounded-lg hover:bg-gray-100 transition" title="Cancella tutte" data-testid="clear-all-notifications">
                      <X size={16} className="text-gray-500" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Grouped Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading && notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
                  <p className="text-sm">Caricamento...</p>
                </div>
              ) : groupedNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="mx-auto mb-2 text-gray-300" size={32} />
                  <p className="text-sm">Nessuna notifica</p>
                </div>
              ) : (
                groupedNotifications.map(([dateLabel, items]) => (
                  <div key={dateLabel}>
                    <div className="px-4 py-2 bg-gray-100 border-b border-t sticky top-0 z-10">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{dateLabel}</span>
                      <span className="text-xs text-gray-400 ml-2">({items.length})</span>
                    </div>
                    {items.map((notification) => (
                      <div
                        key={notification._dbId || notification.id}
                        className={`p-4 border-b hover:bg-gray-50 transition group ${!notification.read ? "bg-blue-50" : ""}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">{getIcon(notification.type)}</div>
                          <div className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => { if (!notification.read && notification._dbId) markAsRead(notification._dbId); }}>
                            <p className={`text-sm ${!notification.read ? "font-semibold" : "font-medium"} text-gray-800`}>
                              {getTitle(notification)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{getDesc(notification)}</p>
                            <span className="text-xs text-gray-400">{formatTime(notification.timestamp)}</span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                            {!notification.read && <div className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0"></div>}
                            {notification._dbId && (
                              <button onClick={(e) => { e.stopPropagation(); deleteNotification(notification._dbId); }}
                                className="p-1 hover:bg-red-50 rounded transition" title="Elimina">
                                <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-2 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <span>{unreadCount > 0 ? `${unreadCount} non lette` : "Tutte lette"}</span>
              <div className="flex items-center gap-3">
                {pushEnabled && <span className="flex items-center gap-1"><BellRing size={10} className="text-green-500" /> Push</span>}
                <span className="flex items-center gap-1">
                  <span className={`h-2 w-2 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`}></span>
                  {isConnected ? "Connesso" : "Offline"}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
